import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "util/services";
import { notification, Spin, Tabs, Checkbox } from "antd";
import { BellOutlined, AlertOutlined, CloseOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import styles from "./style.module.scss";
import { useTranslation } from "react-i18next";
import {
  extractNotificationLink,
  extractNotificationDisplay,
  categoryForTypeId,
} from "./notificationUtils";
import {
  noticeAlertsQueryKeys,
  fetchNoticeAlerts,
  NOTICE_ALERTS_PANEL_LIST_PARAMS,
} from "queries/noticeAlerts";
import AlertPanelItem from "components/Alerts/AlertPanelItem";
import { normalizeAlertList } from "util/cxAlertModel";

const AlertsPanel = ({ onViewAll, user, theme, isDark }) => {
  const { t } = useTranslation();
  const uid = user?.uid;

  const alertsQuery = useQuery({
    queryKey: noticeAlertsQueryKeys.list(uid, NOTICE_ALERTS_PANEL_LIST_PARAMS),
    queryFn: () => fetchNoticeAlerts(NOTICE_ALERTS_PANEL_LIST_PARAMS),
    enabled: Boolean(uid),
    onError: (err) =>
      notification.error({
        message:
          typeof err?.message === "string"
            ? err.message
            : t("notification_page.1"),
      }),
  });

  const alertsList = normalizeAlertList(alertsQuery.data?.alerts ?? []);
  const alertsFetching = alertsQuery.isFetching;
  const hasFiredAlerts = alertsList.some(
    (a) => a.last_payload || a.last_fired_at
  );

  return (
    <>
      <Spin spinning={alertsFetching} style={{ minHeight: 80 }}>
        <div className={styles.alertsPanelBody}>
          {!alertsFetching && alertsList.length === 0 && (
            <div className={styles.alertPanelEmpty}>
              <p>{t("alerts_page.popup_empty_title")}</p>
              <p>{t("alerts_page.popup_empty_hint")}</p>
            </div>
          )}
          {!alertsFetching &&
            alertsList.length > 0 &&
            !hasFiredAlerts && (
              <p className={styles.alertPanelBanner}>
                {t("alerts_page.popup_no_fires")}
              </p>
            )}
          <div className={styles.alertsPanelList}>
            {alertsList.map((record) => (
              <AlertPanelItem
                key={record.alert_id}
                record={record}
                variant="popup"
                theme={theme}
                isDark={isDark}
              />
            ))}
          </div>
        </div>
      </Spin>
      {onViewAll && (
        <div className={styles.viewAllRow}>
          <span
            className={styles.viewAllLink}
            onClick={() => onViewAll("alerts")}
            onKeyDown={(e) => e.key === "Enter" && onViewAll("alerts")}
            role="button"
            tabIndex={0}
          >
            {t("notification_page.view_all_alerts")}
          </span>
        </div>
      )}
    </>
  );
};

const NotificationPage = ({
  theme,
  setShowNotification,
  user,
  filterUnread,
  setFilterUnread,
  notificationList,
  setNotificationList,
  showNoUnreadAvailable,
  setShowNoUnreadAvailable,
  tempList,
  setTempList,
  loading,
  setLoading,
  showEmptyState,
  setShowEmptyState,
  showLast30Days,
  setShowLast30Days,
  invalidateNoticeQueries,
  setNotifsCount,
  onViewAll,
}) => {
  const isDark = theme === "dark";
  const [activeTab, setActiveTab] = useState("notifications");
  const queryClient = useQueryClient();
  const uid = user?.uid;
  const [showBackArrow, setShowBackArrow] = useState(window.innerWidth <= 700);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const notificationRef = useRef();
  const history = useHistory();
  const { t } = useTranslation();
  const headerSubtitle =
    user?.email || user?.fullName || user?.company || "";

  const filteredNotifications = useMemo(() => {
    if (!tempList?.length) return [];
    const seen = new Set();
    const deduped = tempList.filter((n) => {
      if (n.notification_id == null) return true;
      if (seen.has(n.notification_id)) return false;
      seen.add(n.notification_id);
      return true;
    });
    return deduped;
  }, [tempList]);

  const hasActiveFilters = filterUnread === 0;

  const clearFilters = () => {
    if (filterUnread === 0) {
      setShowNoUnreadAvailable(false);
      setFilterUnread(2);
    }
  };

  const unreadCount =
    notificationList?.filter((n) => n.status === 0)?.length || 0;
  const totalCount = notificationList?.length || 0;

  const visibleIds = useMemo(
    () => filteredNotifications.map((n) => n.notification_id),
    [filteredNotifications]
  );
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected =
    visibleIds.some((id) => selectedIds.has(id)) && !allSelected;

  const selectedUnreadCount = filteredNotifications.filter(
    (n) => selectedIds.has(n.notification_id) && n.status === 0
  ).length;

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab, filterUnread, tempList]);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 700) {
        setShowBackArrow(false);
      } else {
        setShowBackArrow(true);
        document.body.style.overflowY = "hidden";
      }
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
      document.body.style.overflowY = "";
    };
  }, []);

  const notificationOutClick = (e) => {
    if (
      notificationRef.current &&
      !notificationRef.current.contains(e.target)
    ) {
      setShowNotification(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", notificationOutClick, true);
    return () =>
      document.removeEventListener("click", notificationOutClick, true);
  }, []);

  const handleMarkAll = async () => {
    setLoading(true);
    try {
      const response = await apiRequest(
        "notice/notification/read-all",
        "POST",
        { user_id: user?.uid },
        true
      );
      if (response.success) {
        await invalidateNoticeQueries?.();
      } else {
        notification.error({ message: t("notification_page.2") });
      }
    } finally {
      setLoading(false);
    }
    setSelectedIds(new Set());
  };

  const updateNotification = async (id, unreadStatus) => {
    const newStatus = unreadStatus ? 0 : 1;
    const flipItem = (n) =>
      n.notification_id === id ? { ...n, status: newStatus } : n;

    const updatedList = notificationList.map(flipItem);
    setNotificationList(updatedList);
    setTempList((prev) => prev.map(flipItem));
    if (setNotifsCount) {
      setNotifsCount(updatedList.filter((n) => n.status === 0).length);
    }

    const response = await apiRequest(
      "notice/notification/read",
      "POST",
      { notification_id: id, unread: unreadStatus },
      true
    );
    if (!response?.success) {
      notification.error({ message: t("notification_page.2") });
      await invalidateNoticeQueries?.();
    }
  };

  const markSelectedAsRead = async () => {
    const targets = filteredNotifications.filter(
      (n) => selectedIds.has(n.notification_id) && n.status === 0
    );
    await Promise.all(
      targets.map((n) => updateNotification(n.notification_id, false))
    );
    setSelectedIds(new Set());
  };

  const openNotificationTarget = (notificationText) => {
    const targetLink = extractNotificationLink(notificationText);
    if (targetLink) {
      if (targetLink.startsWith("/console/")) {
        history.push(targetLink);
        setShowNotification(false);
        return;
      }
      const newTab = window.open();
      if (newTab) {
        newTab.location.href = targetLink.startsWith("/")
          ? `${window.location.origin}${targetLink}`
          : targetLink;
      }
      return;
    }
    const dashboardIdText = notificationText?.split(" : ")[0]?.slice(-5, -1);
    if (dashboardIdText) {
      const newTab = window.open();
      if (newTab) {
        newTab.location.href = `/console/Dashboard/${dashboardIdText}?view_type=trends`;
      }
    }
  };

  const handleFilterUnread = (e) => {
    setShowNoUnreadAvailable(false);
    setFilterUnread(e.target.checked ? 0 : 2);
  };

  const handleShowAllNotifications = () => {
    setShowLast30Days(true);
  };

  const handleRefresh = async () => {
    if (activeTab === "notifications") {
      await invalidateNoticeQueries?.();
    } else if (uid) {
      await queryClient.invalidateQueries({
        queryKey: noticeAlertsQueryKeys.all(uid),
      });
    }
  };

  const onRowActivate = (notif) => {
    openNotificationTarget(notif.notification_Text);
    if (notif.status === 0) {
      updateNotification(notif.notification_id, false);
    }
  };

  const tabItems = [
    {
      key: "notifications",
      label: (
        <span className={styles.tabLabel}>
          <BellOutlined style={{ marginRight: 5 }} />
          {t("nav.notifications")}
          {unreadCount > 0 && (
            <span className={styles.tabUnreadBadge}>{unreadCount}</span>
          )}
        </span>
      ),
      children: (
        <>
          <div className={styles.gscFilterBar}>
            <label className={styles.unreadOnlyToggle}>
              <input
                type="checkbox"
                checked={filterUnread === 0}
                onChange={handleFilterUnread}
              />
              <span>{t("notification_page.4")}</span>
            </label>
          </div>

          <div className={styles.gscBulkBar}>
            <Checkbox
              className={styles.selectAllCheck}
              indeterminate={someSelected}
              checked={allSelected}
              disabled={visibleIds.length === 0}
              aria-label={t("notification_page.select_all")}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedIds(new Set(visibleIds));
                } else {
                  setSelectedIds(new Set());
                }
              }}
            >
              <span className={styles.selectAllText}>
                {t("notification_page.select_all")}
              </span>
            </Checkbox>
            <div className={styles.gscBulkRight}>
              {selectedUnreadCount > 0 ? (
                <button
                  type="button"
                  className={styles.markSelectedLink}
                  onClick={markSelectedAsRead}
                >
                  {t("notification_page.mark_n_read", {
                    count: selectedUnreadCount,
                  })}
                </button>
              ) : (
                <>
                  {totalCount > 0 && (
                    <span className={styles.unreadSummary}>
                      {t("notification_page.unread_status_long", {
                        total: totalCount,
                        unread: unreadCount,
                      })}
                    </span>
                  )}
                  {unreadCount > 0 && (
                    <>
                      <span className={styles.bulkSep}>·</span>
                      <button
                        type="button"
                        className={styles.markAllInline}
                        onClick={handleMarkAll}
                      >
                        {t("notification_page.mark_all_read_short")}
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          <Spin spinning={loading}>
            {showNoUnreadAvailable && (
              <p className={styles.gscEmptyBanner}>{t("notification_page.7")}</p>
            )}
            <div className={styles.gscList}>
              {filteredNotifications.map((notif, index) => {
                const displayText = extractNotificationDisplay(
                  notif.notification_Text
                );
                const hasLink = !!extractNotificationLink(
                  notif.notification_Text
                );
                const unread = notif?.status === 0;
                const isSelected = selectedIds.has(notif.notification_id);
                return (
                  <div
                    key={notif.notification_id || `n-${index}`}
                    className={`${styles.gscListRow} ${
                      isSelected ? styles.gscListRowSelected : ""
                    }`}
                  >
                    <div
                      className={styles.gscRowCheck}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      <Checkbox
                        checked={selectedIds.has(notif.notification_id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (checked) {
                              next.add(notif.notification_id);
                            } else {
                              next.delete(notif.notification_id);
                            }
                            return next;
                          });
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className={styles.gscRowMainBtn}
                      onClick={() => onRowActivate(notif)}
                    >
                      <div className={styles.gscRowTextBlock}>
                        <div
                          className={`${styles.gscRowTitle} ${
                            unread ? styles.gscRowTitleUnread : ""
                          }`}
                          title={displayText}
                        >
                          {displayText}
                        </div>
                        <div className={styles.gscRowMeta}>
                          {notif.date} · {categoryForTypeId(notif.type_id, t)}
                          {hasLink && (
                            <>
                              {" "}
                              ·{" "}
                              <span className={styles.gscRowViewHint}>
                                {t("notification_page.view_btn")}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </button>
                  </div>
                );
              })}
              {!loading && !showLast30Days && notificationList?.length > 5 && (
                <div
                  className={styles.lastDayLabel}
                  onClick={handleShowAllNotifications}
                  onKeyDown={(e) =>
                    e.key === "Enter" && handleShowAllNotifications()
                  }
                  role="button"
                  tabIndex={0}
                >
                  {t("notification_page.8")}
                </div>
              )}
              {!loading &&
                tempList?.length > 0 &&
                filteredNotifications.length === 0 &&
                hasActiveFilters && (
                  <div className={styles.gscFilterEmpty}>
                    <p>{t("notification_page.filter_empty_title")}</p>
                    <button
                      type="button"
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      {t("notification_page.clear_filters")}
                    </button>
                  </div>
                )}
              {showEmptyState && !loading && !showNoUnreadAvailable && (
                <p className={styles.gscEmptyBanner}>
                  {t("notification_page.9")}
                </p>
              )}
            </div>
          </Spin>
          {onViewAll && (
            <div className={styles.viewAllRow}>
              <span
                className={styles.viewAllLink}
                onClick={() => onViewAll("notifications")}
                onKeyDown={(e) =>
                  e.key === "Enter" && onViewAll("notifications")
                }
                role="button"
                tabIndex={0}
              >
                {t("notification_page.view_all_notifications")}
              </span>
            </div>
          )}
        </>
      ),
    },
    {
      key: "alerts",
      label: (
        <span className={styles.tabLabel}>
          <AlertOutlined style={{ marginRight: 5 }} />
          {t("nav.alerts")}
        </span>
      ),
      children: (
        <AlertsPanel onViewAll={onViewAll} user={user} theme={theme} isDark={isDark} />
      ),
    },
  ];

  return (
    <div
      className={`${styles.notificationContainer} ${
        isDark ? styles.notificationContainerDark : ""
      }`}
      ref={notificationRef}
    >
      <div className={styles.gscHeader}>
        <div className={styles.gscHeaderMain}>
          {showBackArrow && (
            <button
              type="button"
              className={styles.gscBackBtn}
              aria-label="Back"
              onClick={() => setShowNotification(false)}
            >
              <i className="fa-solid fa-arrow-left" />
            </button>
          )}
          <div>
            <h2 className={styles.gscTitle}>
              {t("notification_page.panel_main_title")}
            </h2>
            {headerSubtitle ? (
              <div className={styles.gscSubtitle}>{headerSubtitle}</div>
            ) : null}
          </div>
        </div>
        <div className={styles.gscHeaderActions}>
          <button
            type="button"
            className={styles.gscIconBtn}
            onClick={handleRefresh}
            aria-label="Refresh"
          >
            <img
              src={isDark ? "/assets/refresh.svg" : "/assets/refresh_black.svg"}
              alt=""
            />
          </button>
          {activeTab === "notifications" && (
            <button
              type="button"
              className={styles.gscIconBtn}
              onClick={() => {
                setShowNotification(false);
                history.push("/settings/preferences");
              }}
              aria-label="Settings"
            >
              <img
                src={
                  isDark ? "/assets/settings.svg" : "/assets/settings_black.svg"
                }
                alt=""
              />
            </button>
          )}
          <button
            type="button"
            className={styles.gscCloseBtn}
            onClick={() => setShowNotification(false)}
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        className={styles.popupTabs}
        size="small"
      />
    </div>
  );
};

export default NotificationPage;
